import { app } from '@df/core/app';
import { ColorOptions, LayoutWidthOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NCmsComponentRow } from './cms-component-row.interface';

export const CMS_COMPONENT_CMS_CONFIG = <Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'>>{
    id: NCmsComponentRow.ID,
    isAdvanced: true,
    name: 'Section',
    description: 'Component is optimized to work with footer',
    image: '/common/cms/row-component.png',
    tags: ['row'],
    fields: [
        // general
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            title: 'Columns',
            key: 'columns',
            itemName: 'column',
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Size',
                    key: 'size',
                    description: `Sizes are relative to other columns, column with size 3 is 3 times wider then column with size 1.`,
                    options: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(number => {
                        return { key: number, title: `${number}` };
                    }),
                    default: 12
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Offset',
                    key: 'offset',
                    description: `Column offset from left`,
                    options: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(number => {
                        return { key: number, title: `${number}` };
                    }),
                    default: 0
                },
                <NICmsContentField.IComponentsField>{
                    type: NICmsContentField.EFieldType.components,
                    title: 'Components',
                    itemName: 'component',
                    key: 'components',
                    default: []
                }
            ],
            defaultSize: 1,
            maxSize: 12
        },
        // grid settings
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-grid',
            title: 'Grid Settings'
        },
        <NICmsContentField.INumberField>{
            key: 'grid_size',
            type: NICmsContentField.EFieldType.number,
            title: 'Grid Size',
            default: 12,
            min: 1,
            max: 24
        },
        <NICmsContentField.INumberField>{
            key: 'grid_gap',
            type: NICmsContentField.EFieldType.number,
            title: 'Grid Gap',
            description: `Space between columns (base size * grid gap)`,
            default: 4,
            min: 0,
            max: 16
        },
        <NICmsContentField.IField>{
            key: 'grid_columns_wrap',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Grid Columns Wrap',
            description: `Move columns to the next row if there is no free space`,
            default: true
        },
        <NICmsContentField.IChoiceField>{
            key: 'align_vertical',
            type: NICmsContentField.EFieldType.choice,
            title: 'Content Align Vertical',
            options: [
                ...UndefinedOption,
                { key: 'flex-top', title: 'Top' },
                { key: 'flex-middle', title: 'Middle' },
                { key: 'flex-bottom', title: 'Bottom' }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'width',
            type: NICmsContentField.EFieldType.choice,
            title: 'Content Width',
            itemName: 'width',
            options: LayoutWidthOptions,
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'color',
            title: 'Color',
            type: NICmsContentField.EFieldType.choice,
            colorPreview: true,
            options: ColorOptions,
            description: 'Text color'
        },
        <NICmsContentField.IField>{
            key: 'element_id',
            title: 'Element Id',
            type: NICmsContentField.EFieldType.text
        },
        {
            key: 'styles.bg',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'Background Color',
            group: NICmsContentComponent.EFieldGroup.styles,
            previewMode: 'color',
            options: () => app.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG).colors,
            default: undefined
        }
    ]
};
